import React, { useState } from "react";
import {
    Atom, TestTube, Leaf, FunctionSquare, Calculator, Sprout, TrendingUp, Cross,
    PenTool, Book, Microscope, Monitor, Globe, UsersIcon, Heart, Briefcase, Palette,
    Home, BookOpen, FileText, BarChart3, Building2, Coffee, Activity,
    Check, X, Star, Crown, Zap, Users, Trophy, Target, Gift,
    Play,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const PricingAndSubjectsPage = () => {
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState('premium');
    const [isAnnual, setIsAnnual] = useState(false);

    const subjectIcons = {
        Physics: Atom,
        Chemistry: TestTube,
        Biology: Leaf,
        "Further Mathematics": FunctionSquare,
        Mathematics: Calculator,
        "Agricultural Science": Sprout,
        Commerce: TrendingUp,
        CRS: Cross,
        "English Language": PenTool,
        Literature: BookOpen,
        "Basic Science": Microscope,
        "Basic Technology": Monitor,
        "Social Studies": Globe,
        "Civic Education": UsersIcon,
        "Computer Science": Monitor,
        "Physical and Health Education": Heart,
        "Business Studies": Briefcase,
        "Creative Arts": Palette,
        "Home Economics": Home,
        Geography: Globe,
        History: FileText,
        Economics: BarChart3,
        Government: Building2,
        French: Coffee,
        Music: Activity,
    };

    // const pricingPlans = [
    //     {
    //         name: "Free Starter",
    //         price: { monthly: 0, annual: 0 },
    //         description: "Perfect for getting started with basic learning",
    //         icon: <Gift className="w-6 h-6" />,
    //         color: "from-gray-500 to-gray-600",
    //         popular: false,
    //         features: [
    //             "Access to 10 past questions per subject for JSS1–JSS3",
    //             "Free for 1 month or until you exhaust your question sets (whichever comes first)",
    //             "Study with friends and family"
    //         ],
    //         limitations: [
    //             "Cannot create groups",
    //             "Cannot see leaderboard",
    //             "Cannot search questions",
    //             "Limited to JSS subjects only",
    //             "Basic explanations only"
    //         ]
    //     },
    //     {
    //         name: "Premium",
    //         price: { monthly: 4500, annual: 40000 }, // annual ≈ 2 free months
    //         description: "Most popular choice for serious students",
    //         icon: <Star className="w-6 h-6" />,
    //         color: "from-blue-500 to-blue-600",
    //         popular: true,
    //         features: [
    //             "500+ past questions across WAEC, JAMB, and NECO",
    //             "All subjects JSS1–SS3",
    //             "Detailed step-by-step solutions",
    //             "Performance analytics",
    //             // "Create groups and compete with friends",
    //             "Search questions easily",
    //             // "See leaderboard",
    //             "Exam preparation guides",
    //             "Regular feature updates",
    //             // "Study with friends and family"
    //         ],
    //         limitations: []
    //     },
    //     {
    //         name: "Premium Plus",
    //         price: { monthly: 7000, annual: 70000 }, // annual ≈ 2 free months
    //         description: "Ultimate package with exclusive features",
    //         icon: <Crown className="w-6 h-6" />,
    //         color: "from-purple-500 to-purple-600",
    //         popular: false,
    //         features: [
    //             "Everything in Premium",
    //             "Custom study plans (personalized schedules based on subjects, weak areas, and upcoming exams)",
    //             "Unlimited practice tests",
    //             "Early access to new features",
    //             "Parent/guardian progress reports",
    //             // "Certificate upon completion",
    //             // "Exam success guarantee*",
    //             "Study with friends and family"
    //         ],
    //         limitations: []
    //     }
    // ];


    const pricingPlans = [
        {
            name: "Free",
            price: { monthly: 0, annual: 0 },
            description: "Generous access for independent learners",
            icon: <Gift className="w-6 h-6" />,
            color: "from-gray-500 to-gray-600",
            popular: false,
            features: [
                "Access to about 5,000 past questions per subject",
                "Covers JSS1–SS3 subjects",
                "Basic explanations for answers",
                "Practice at your own pace",
                "Study with friends and family"
            ],
            limitations: [
                "Cannot create groups",
                "No advanced performance analytics",
                "No personalized study plans"
            ]
        },
        {
            name: "Premium Plus",
            price: { monthly: 7000, annual: 70000 },
            description: "Unlimited access for serious exam preparation",
            icon: <Crown className="w-6 h-6" />,
            color: "from-purple-500 to-purple-600",
            popular: true,
            features: [
                "Access to significantly more than 5,000 questions per subject",
                "All subjects JSS1–SS3 plus exam-focused collections",
                "Create and manage study groups",
                "Detailed step-by-step solutions",
                "Unlimited practice tests",
                "Custom study plans based on strengths and weaknesses",
                "Advanced performance analytics",
                "Study with friends and family",
                "Priority access to new features"
            ],
            limitations: []
        }
    ];


    const popularSubjects = [
        { name: "Mathematics", category: "Core", students: "850" },
        { name: "English Language", category: "Core", students: "920" },
        { name: "Physics", category: "Science", students: "680" },
        { name: "Chemistry", category: "Science", students: "620" },
        { name: "Biology", category: "Science", students: "710" },
        { name: "Economics", category: "Arts", students: "590" },
        { name: "Government", category: "Arts", students: "480" },
        { name: "Literature", category: "Arts", students: "420" },
        { name: "Geography", category: "Arts", students: "390" },
        { name: "Further Mathematics", category: "Science", students: "320" },
        { name: "Commerce", category: "Commercial", students: "410" },
        { name: "Agricultural Science", category: "Science", students: "280" }
    ];

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 0
        }).format(price);
    };

    return (
        <div className="bg-gradient-to-br from-blue-50 to-white">
            {/* Hero Section */}
            <section
                className="py-24 pt-32 text-white relative overflow-hidden bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
                }}
            >
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/80"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center max-w-4xl mx-auto">
                        <h1 className="text-5xl md:text-6xl font-extrabold mb-6">
                            Choose Your Learning
                            <span className="block text-blue-200">Journey</span>
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 opacity-90 leading-relaxed">
                            Flexible pricing plans for every student's needs. From free starter to premium tutoring -
                            find the perfect plan to ace your exams.
                        </p>
                        <div className="flex items-center justify-center gap-4 mb-8">
                            <span className={`font-medium ${!isAnnual ? 'text-white' : 'text-blue-200'}`}>Monthly</span>
                            <button
                                onClick={() => setIsAnnual(!isAnnual)}
                                className={`relative w-16 h-8 rounded-full transition-all ${isAnnual ? 'bg-blue-200' : 'bg-blue-400'}`}
                            >
                                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${isAnnual ? 'translate-x-8' : ''}`}></div>
                            </button>
                            <span className={`font-medium ${isAnnual ? 'text-white' : 'text-blue-200'}`}>Annual</span>
                            {isAnnual && (
                                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                    Save 15%!
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Plans */}
            {/* <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {pricingPlans.map((plan, index) => (
                            <div
                                key={index}
                                className={`relative bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 ${plan.popular ? 'ring-4 ring-[#2148C0] ring-opacity-20 scale-105' : ''
                                    }`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                        <div className="bg-[#2148C0] text-white px-6 py-2 rounded-full text-sm font-semibold flex items-center gap-1">
                                            <Star className="w-4 h-4" />
                                            Most Popular
                                        </div>
                                    </div>
                                )}

                                <div className="p-8">
                                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${plan.color} flex items-center justify-center text-white mb-4`}>
                                        {plan.icon}
                                    </div>

                                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                                    <p className="text-gray-600 mb-6">{plan.description}</p>

                                    <div className="mb-6">
                                        <div className="text-4xl font-bold text-gray-800">
                                            {formatPrice(isAnnual ? plan.price.annual : plan.price.monthly)}
                                            {plan.price.monthly > 0 && (
                                                <span className="text-lg font-normal text-gray-500">
                                                    /{isAnnual ? 'year' : 'month'}
                                                </span>
                                            )}
                                        </div>
                                        {isAnnual && plan.price.monthly > 0 && (
                                            <div className="text-sm text-green-600">
                                                Save {formatPrice((plan.price.monthly * 12) - plan.price.annual)} yearly!
                                            </div>
                                        )}
                                    </div>

                                    <ul className="space-y-3 mb-8">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                                <span className="text-gray-700">{feature}</span>
                                            </li>
                                        ))}
                                        {plan.limitations.map((limitation, i) => (
                                            <li key={i} className="flex items-start gap-3 opacity-60">
                                                <X className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <span className="text-gray-500">{limitation}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() =>
                                            plan.name === "Free Starter"
                                                ? navigate("/signup")
                                                // ? navigate("/free-login")
                                                : navigate("/signup")
                                        }
                                        className={`w-full py-4 rounded-2xl font-semibold transition-all ${plan.popular
                                            ? "bg-[#2148C0] text-white hover:bg-blue-700 transform hover:scale-105"
                                            : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                            }`}
                                    >
                                        {plan.name === "Free Starter" ? "Get Started Free" : `Choose ${plan.name}`}
                                    </button>


                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {pricingPlans.map((plan, index) => (
                            <div
                                key={index}
                                className={`relative bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 ${plan.popular
                                    ? "ring-4 ring-[#2148C0] ring-opacity-20 scale-105"
                                    : ""
                                    }`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                        <div className="bg-[#2148C0] text-white px-6 py-2 rounded-full text-sm font-semibold flex items-center gap-1">
                                            <Star className="w-4 h-4" />
                                            Most Popular
                                        </div>
                                    </div>
                                )}

                                <div className="p-8">
                                    <div
                                        className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${plan.color} flex items-center justify-center text-white mb-4`}
                                    >
                                        {plan.icon}
                                    </div>

                                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                        {plan.name}
                                    </h3>
                                    <p className="text-gray-600 mb-6">
                                        {plan.description}
                                    </p>

                                    {/* PRICE */}
                                    <div className="mb-6">
                                        <div className="text-4xl font-bold text-gray-800">
                                            {formatPrice(
                                                isAnnual
                                                    ? plan.price.annual
                                                    : plan.price.monthly
                                            )}
                                            {plan.price.monthly > 0 && (
                                                <span className="text-lg font-normal text-gray-500">
                                                    /{isAnnual ? "year" : "month"}
                                                </span>
                                            )}
                                        </div>

                                        {isAnnual && plan.price.monthly > 0 && (
                                            <div className="text-sm text-green-600 mt-1">
                                                Save{" "}
                                                {formatPrice(
                                                    plan.price.monthly * 12 -
                                                    plan.price.annual
                                                )}{" "}
                                                yearly
                                            </div>
                                        )}
                                    </div>

                                    {/* FEATURES */}
                                    <ul className="space-y-3 mb-8">
                                        {plan.features.map((feature, i) => (
                                            <li
                                                key={i}
                                                className="flex items-start gap-3"
                                            >
                                                <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                                <span className="text-gray-700">
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}

                                        {plan.limitations.map((limitation, i) => (
                                            <li
                                                key={i}
                                                className="flex items-start gap-3 opacity-60"
                                            >
                                                <X className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <span className="text-gray-500">
                                                    {limitation}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA */}
                                    <button
                                        onClick={() => {
                                            if (plan.price.monthly > 0) {
                                                localStorage.setItem("selectedPlan", "premium");
                                            } else {
                                                localStorage.setItem("selectedPlan", "free");
                                            }

                                            navigate("/signup");
                                        }}
                                        className={`w-full py-4 rounded-2xl font-semibold transition-all ${plan.popular
                                            ? "bg-[#2148C0] text-white hover:bg-blue-700 transform hover:scale-105"
                                            : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                            }`}
                                    >
                                        {plan.price.monthly === 0
                                            ? "Get Started Free"
                                            : "Upgrade to Premium Plus"}
                                    </button>

                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* Popular Subjects */}
            <section
                className="py-24 bg-cover bg-center bg-fixed relative"
                style={{
                    backgroundImage: 'url("https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")'
                }}
            >
                <div className="absolute inset-0 bg-black bg-opacity-75"></div>
                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-white mb-4">Subjects We Cover</h2>
                        <p className="text-xl text-blue-100">Comprehensive coverage from JSS1 to SS3</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                        {popularSubjects.map((subject, index) => {
                            const Icon = subjectIcons[subject.name] || Book;
                            return (
                                <div
                                    key={index}
                                    className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 p-6 rounded-2xl text-center hover:bg-opacity-20 transition-all cursor-pointer group"
                                >
                                    <Icon className="w-8 h-8 text-blue-200 mx-auto mb-3 group-hover:text-white transition-colors" />
                                    <h3 className="text-white font-semibold mb-1">{subject.name}</h3>
                                    <p className="text-blue-200 text-sm mb-2">{subject.category}</p>
                                    <div className="flex items-center justify-center gap-1 text-blue-100 text-xs">
                                        <Users className="w-3 h-3" />
                                        {subject.students} students
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {/* 
                    <div className="text-center mt-12">
                        <button
                            // onClick={() => navigate("/subjects")}
                            className="bg-white text-[#2148C0] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all inline-flex items-center gap-2"
                        >
                            View All Subjects <ChevronRight className="w-4 h-4" />
                        </button>
                    </div> */}
                </div>
            </section>

            {/* Why Choose Our Platform */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Students Choose TeeChaa</h2>
                        <p className="text-xl text-gray-600">The complete learning solution for Nigerian students</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Trophy className="w-8 h-8 text-[#2148C0]" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">95% Improvement Rate</h3>
                            <p className="text-gray-600">Students achieve their target scores</p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Target className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">400K questions across different classes</h3>
                            <p className="text-gray-600">From WAEC, JAMB, and NECO standard</p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Play className="w-8 h-8 text-purple-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">HD Video Lessons</h3>
                            <p className="text-gray-600">Clear explanations from expert teachers</p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Zap className="w-8 h-8 text-yellow-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">24/7 Access</h3>
                            <p className="text-gray-600">Study anytime, anywhere</p>
                        </div>
                    </div>
                </div>
            </section>


            {/* Final CTA */}
            <section className="py-20 bg-[#2148C0] text-white text-center">
                <div className="container mx-auto px-6">
                    <h2 className="text-4xl font-bold mb-6">Ready to Start Your Success Story?</h2>
                    <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                        Join over 10,000 Nigerian students who are achieving their academic dreams with TeeChaa.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => navigate("/signup")}
                            className="bg-white text-[#2148C0] px-10 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105"
                        >
                            Start Free Trial
                        </button>
                        {/* <button
                            onClick={() => navigate("/#waitlist")}
                            className="bg-white text-[#2148C0] px-10 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105"
                        >
                            Start Free Trial
                        </button> */}
                        <button
                            onClick={() => navigate("/contact")}
                            className="border-2 border-white text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-[#2148C0] transition-all"
                        >
                            Contact Sales
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PricingAndSubjectsPage;